import axios from "axios";
import StorageManager from "../../../utils/StorageManager";
import { PROVIDER_CONFIG } from "./providerConfig";
import { WILD_SYMBOL_ID, DEFAULT_SLOTFACE } from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { Settings } from "../../../settings";

const { SYMBOL_MAP } = PROVIDER_CONFIG;

/* -------------------------------------------------------
   WORKER FACTORY AS BLOB
------------------------------------------------------- */
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

/* -------------------------------------------------------
   INLINE spinWorker.js

   Worker sends raw API response - transformation happens in spin() method.
   No Authorization header needed - sessionId in URL path handles authentication.
------------------------------------------------------- */
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, baseUrl, gameId, sessionId, currency, funMode, purchasedFeature } = event.data;

    if (!bet || bet <= 0 || !baseUrl || !gameId || !sessionId || !currency) {
      postMessage({ error: "PixUpPlayRGS: Missing required parameters" });
      return;
    }

    try {
      const apiUrl = \`\${baseUrl}/game-api/\${gameId}/api/session/\${sessionId}/game/spin\`;

      const body = {
        betAmount: bet,
        currency: currency,
        funMode: funMode
      };
      if (purchasedFeature) body.createFreeSpin = true;
      const response = await axios.post(apiUrl, body);

      postMessage(response.data);

    } catch (error) {
      if (error.response?.data) {
        postMessage({ error: error.response.data });
      } else {
        postMessage({ error: "PixUpPlayRGS: Spin API call failed: " + error.message });
      }
    }
  };
`;

/* -------------------------------------------------------
   MAIN RGS PROVIDER
------------------------------------------------------- */
class PixUpPlayRGS {
  /**
   * Constructor - Reads configuration from window.__OPTIONS__
   *
   * Expects window.__OPTIONS__ to contain:
   * - sessionId: Session identifier
   * - gameId: Game identifier
   * - baseUrl: API base URL
   * - currency: Currency code (optional, defaults to "USD")
   * - funMode: Fun/demo mode flag (optional, defaults to false)
   */
  constructor() {
    if (!window.__OPTIONS__) {
      throw new Error("PixUpPlayRGS: window.__OPTIONS__ not found");
    }

    const { sessionId, gameId, baseUrl, currency, funMode } =
      window.__OPTIONS__;

    if (!sessionId || !gameId || !baseUrl) {
      throw new Error(
        "PixUpPlayRGS: Missing required fields in window.__OPTIONS__ (sessionId, gameId, baseUrl)",
      );
    }

    this.sessionId = sessionId;
    this.gameId = gameId;
    this.baseUrl = baseUrl;
    this.currency = currency || "USD";
    this.funMode = funMode || false;
    this.worker = null;
  }

  /**
   * Generate default slotface for init when API provides no initial reels.
   * Uses l1-l5 (one per reel) so no winning combination is possible at init.
   * @returns {Object} Round object with reels, wins, win
   */
  generateDefaultSlotface() {
    return {
      reels: DEFAULT_SLOTFACE,
      wins: [],
      win: 0,
    };
  }

  /**
   * Convert PixUpPlay string symbol IDs to game numeric IDs
   * @param {string[][]} grid - visibleGrid with string IDs
   * @returns {number[][]} Grid with numeric IDs
   */
  transformVisibleGrid(grid) {
    if (!grid || !Array.isArray(grid)) return [];

    return grid.map((reel) => {
      if (!Array.isArray(reel)) return [];
      return reel.map((symbolId) => {
        const numericId = SYMBOL_MAP[symbolId];
        return numericId !== undefined ? numericId : 0;
      });
    });
  }

  /**
   * Transform PixUpPlay wins to Redux-compatible format
   * @param {Array} wins - PixUpPlay wins array
   * @returns {Array<Object>} Transformed wins
   */
  transformWins(wins) {
    if (!wins || !Array.isArray(wins)) return [];

    const freeSpinsEnabled = FEATURES_CONFIG.FREE_SPINS.ENABLED;

    const transformed = wins
      .map((win) => {
        let numericSymbolId;
        if (typeof win.symbolId === "number") {
          numericSymbolId = win.symbolId;
        } else {
          numericSymbolId = SYMBOL_MAP[win.symbolId];
        }

        if (numericSymbolId === undefined || numericSymbolId === null) {
          return null;
        }

        const result = {
          symbol: numericSymbolId,
          win: win.payout,
          positions: win.positions,
          id: win.lineIndex - 1, // API is 1-based (1-20), spine animations are 0-based (win_line_0–win_line_19)
        };

        // Only tag scatter wins when free spins feature needs them
        if (freeSpinsEnabled) {
          result.isScatter = win.symbolId === "F";
        }

        return result;
      })
      .filter((win) => win !== null);

    // Sort: scatters last (only when free spins enabled), everything else by payout descending
    if (freeSpinsEnabled) {
      transformed.sort((a, b) => {
        if (a.isScatter && !b.isScatter) return 1;
        if (!a.isScatter && b.isScatter) return -1;
        return b.win - a.win;
      });
    } else {
      transformed.sort((a, b) => b.win - a.win);
    }

    return transformed;
  }

  /**
   * Transform response: convert string symbol IDs to numeric IDs
   *
   * Just converts visibleGrid string IDs to numbers via SYMBOL_MAP.
   * No trimming, no buffers — data arrives exactly as needed.
   *
   * @param {Object} response - Raw API response
   * @returns {Object} Response with transformed reels added
   */
  transformReelsSymbols(response) {
    if (
      response.data?.spinResult?.visibleGrid &&
      Array.isArray(response.data.spinResult.visibleGrid)
    ) {
      response.data.spinResult.reels = response.data.spinResult.visibleGrid.map(
        (reel) => {
          if (!Array.isArray(reel)) return [];
          return reel.map((symbolId) => {
            const numericId = SYMBOL_MAP[symbolId];
            return numericId !== undefined ? numericId : 0;
          });
        },
      );
    }
    return response;
  }

  /**
   * Extract scatter positions from visibleGrid using SYMBOL_MAP
   * Returns array of [reelIndex, symbolIndex] pairs for each scatter symbol found
   * @param {string[][]} visibleGrid - Raw grid with string symbol IDs
   * @returns {number[][]} Array of [reelIndex, symbolIndex] pairs
   */
  extractScatterPositions(visibleGrid) {
    if (!visibleGrid || !Array.isArray(visibleGrid)) return [];

    const positions = [];
    const scatterNumericId = SYMBOL_MAP["F"]; // F → 12 (scatter)

    for (let reelIndex = 0; reelIndex < visibleGrid.length; reelIndex++) {
      const reel = visibleGrid[reelIndex];
      if (!Array.isArray(reel)) continue;

      for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
        if (SYMBOL_MAP[reel[symbolIndex]] === scatterNumericId) {
          positions.push([reelIndex, symbolIndex]);
        }
      }
    }

    return positions;
  }

  /**
   * Extract scatter count from API scatterCounts, converting string IDs via SYMBOL_MAP
   * Returns total count for scatter symbols (numeric ID)
   * @param {Object} scatterCounts - API scatter counts (e.g., {F: 2})
   * @returns {number} Total scatter count
   */
  extractScatterCount(scatterCounts) {
    if (!scatterCounts || typeof scatterCounts !== "object") return 0;

    let totalCount = 0;
    for (const [stringId, count] of Object.entries(scatterCounts)) {
      const numericId = SYMBOL_MAP[stringId];
      // Only count symbols that map to known scatter IDs
      if (numericId !== undefined) {
        totalCount += count;
      }
    }
    return totalCount;
  }

  /**
   * Compute win type from win amount and bet using provider thresholds
   * Returns "regular_win" for normal wins, or "big_win", "mega_win", etc. for animated wins
   * @param {number} winAmount - Total win amount
   * @param {number} betAmount - Bet amount
   * @returns {string} Win type string
   */
  computeWinType(winAmount, betAmount) {
    if (!winAmount || winAmount <= 0 || !betAmount || betAmount <= 0) return "";

    const multiplier = winAmount / betAmount;
    const thresholds = PROVIDER_CONFIG.WIN_THRESHOLDS;
    const winTypes = PROVIDER_CONFIG.WIN_TYPES;

    // Sort thresholds highest to lowest for first-match
    const sorted = Object.entries(thresholds).sort(([, a], [, b]) => b - a);

    for (const [thresholdKey, thresholdValue] of sorted) {
      if (multiplier >= thresholdValue) {
        const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
        return winTypes[winTypeKey] || "regular_win";
      }
    }

    // Win exists but below all animated thresholds
    return "regular_win";
  }

  /**
   * Detect which middle reels (1, 2, 3) have wild symbols that are part of a connected winning line.
   * Pre-computes expanding wild data so downstream UI consumers don't need to calculate.
   *
   * @param {number[][]} reels - Transformed reels with numeric symbol IDs
   * @param {Array<Object>} wins - Transformed wins array
   * @returns {Array<{reelIndex: number, wildPosition: number}>} Reels with expanding wilds
   */
  detectExpandingWilds(reels, wins) {
    if (!reels || !wins || !Array.isArray(wins) || wins.length === 0) {
      return [];
    }

    if (WILD_SYMBOL_ID === null) {
      return [];
    }

    // Dynamic middle reel indices — all reels except first and last
    const middleReelIndices = Array.from(
      { length: Math.max(0, Settings.REEL_COUNT - 2) },
      (_, i) => i + 1
    );
    const reelsWithWilds = [];

    for (const reelIndex of middleReelIndices) {
      const visibleSymbols = reels[reelIndex];
      if (!visibleSymbols || !Array.isArray(visibleSymbols)) {
        continue;
      }

      // Find first wild position in visible symbols
      const wildPosition = visibleSymbols.findIndex(
        (symbolId) => symbolId === WILD_SYMBOL_ID,
      );

      if (wildPosition === -1) continue;

      // Check if wild is part of a connected winning line
      const isPartOfConnectedWin = wins.some((win) => {
        if (!Array.isArray(win.positions) || win.positions.length === 0) {
          return false;
        }

        const reelsInWin = win.positions.map(([r]) => r);
        const uniqueReels = [...new Set(reelsInWin)].sort((a, b) => a - b);

        if (!uniqueReels.includes(reelIndex)) {
          return false;
        }

        // Find the rightmost connected reel (consecutive from left)
        let rightmostConnected = -1;
        for (let i = 0; i < uniqueReels.length; i++) {
          const currentReel = uniqueReels[i];
          if (i === 0 && currentReel > 1) break;
          if (i > 0 && currentReel !== uniqueReels[i - 1] + 1) break;
          rightmostConnected = currentReel;
        }

        return rightmostConnected >= reelIndex;
      });

      if (isPartOfConnectedWin) {
        reelsWithWilds.push({ reelIndex, wildPosition });
      }
    }

    return reelsWithWilds;
  }

  /**
   * Adapt spin data to state format
   * Uses transformed reels (with buffers) from transformReelsSymbols
   * Pre-computes scatter count, positions, win type, and expanding wilds
   * so downstream consumers don't need to calculate
   *
   * @param {Object} spinResult - Transformed spin result
   * @returns {Object} State-compatible round
   */
  adaptSpinRoundToState(spinResult, bet) {
    if (!spinResult) return null;

    const totalWin = spinResult.totalWin || 0;
    const winType = this.computeWinType(totalWin, bet);
    const reels =
      spinResult.reels || this.transformVisibleGrid(spinResult.visibleGrid);
    const wins = this.transformWins(spinResult.wins);

    const round = {
      reels,
      wins,
      win: totalWin,
      bet: bet,
      winType,
      isAnimatedWin:
        winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
    };

    // Scatter detection — only when free spins feature is enabled
    if (FEATURES_CONFIG.FREE_SPINS.ENABLED) {
      round.scatterCount = this.extractScatterCount(spinResult.scatterCounts);
      round.scatterPositions = this.extractScatterPositions(spinResult.visibleGrid);
      // Free spin round tracking (present on every free spin response)
      if (spinResult.freeSpinIndex !== undefined) {
        round.freeSpinIndex = spinResult.freeSpinIndex;
      }
      if (spinResult.freeSpinTotal !== undefined) {
        round.freeSpinTotal = spinResult.freeSpinTotal;
      }
      if (spinResult.freeSpinsAwarded !== undefined) {
        round.freeSpinsAwarded = spinResult.freeSpinsAwarded;
      }
      if (spinResult.roundTotalWin !== undefined) {
        round.roundTotalWin = spinResult.roundTotalWin;
      }
    }

    // Expanding wild detection — only when expanding wilds feature is enabled
    if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED) {
      round.expandingWildReels = this.detectExpandingWilds(reels, wins);
      round.hasExpandingWilds = round.expandingWildReels.length > 0;
    }

    return round;
  }

  /**
   * Initialize game session with PixUpPlay
   *
   * @returns {Promise<Object>} Normalized game initialization data
   */
  async initGame() {
    const apiUrl = `${this.baseUrl}/game-api/${this.gameId}/api/session/${this.sessionId}/game/init`;

    const response = await axios.post(apiUrl, { funMode: this.funMode });

    if (response.data?.success === false) {
      const errorMsg = response.data.error || "Unknown API error";
      throw new Error(`PixUpPlayRGS: Init - API error: ${errorMsg}`);
    }

    const data = response.data?.data;
    if (!data) {
      throw new Error(
        "PixUpPlayRGS: Init - Invalid response: missing data object",
      );
    }

    const {
      balance,
      currency,
      betLevels,
      startWithBetLevel,
      paylines,
      paytable,
      rtp,
    } = data;

    // Validate required fields
    if (
      balance === undefined ||
      !currency ||
      !betLevels ||
      !startWithBetLevel ||
      !paylines?.patterns ||
      !paytable
    ) {
      throw new Error(
        "PixUpPlayRGS: Init - Invalid response: missing required fields",
      );
    }

    // Calculate initial bet (startWithBetLevel is 1-based)
    const initialBetIndex = startWithBetLevel - 1;
    const initialBet = betLevels[initialBetIndex];
    if (initialBet === undefined) {
      throw new Error(
        `PixUpPlayRGS: Init - Invalid startWithBetLevel: ${startWithBetLevel}`,
      );
    }

    // Session validation
    this.validateSession();

    // Check for saved round
    const savedRound = StorageManager.loadBaseGameRound();
    const round = savedRound?.round || this.generateDefaultSlotface();

    // Paylines come as [row, row, row, row, row] per line — use directly
    const winningLines = paylines.patterns;

    // Normalize paytable: string IDs → numeric IDs, payouts object → array
    const normalizedPaytable = {};
    const paytableEntries = Array.isArray(paytable)
      ? paytable.map((entry) => [entry.symbolId, entry.payouts])
      : Object.entries(paytable);

    paytableEntries.forEach(([stringId, payoutsData]) => {
      const numericId = SYMBOL_MAP[stringId];
      if (
        numericId !== undefined &&
        payoutsData &&
        typeof payoutsData === "object"
      ) {
        const payoutsArray = [];
        Object.entries(payoutsData).forEach(([count, payout]) => {
          const index = parseInt(count, 10);
          if (!isNaN(index) && index > 0) {
            payoutsArray[index - 1] = payout;
          }
        });
        // Fill undefined slots with 0
        for (let i = 0; i < payoutsArray.length; i++) {
          if (payoutsArray[i] === undefined) {
            payoutsArray[i] = 0;
          }
        }
        normalizedPaytable[numericId] = payoutsArray;
      }
    });

    return {
      bet: savedRound?.bet || initialBet,
      balance,
      token: this.sessionId,
      currency,
      game: this.gameId,
      bets: betLevels,
      winningLines,
      paytable: normalizedPaytable,
      round,
      rtp: rtp || null,
    };
  }

  /**
   * Get spin parameters - PixUpPlay has no command/purchasedFeature, always base game
   * @returns {Object} { command, spinOptions }
   */
  getSpinParams() {
    return { command: "spin", spinOptions: undefined };
  }

  /**
   * Execute spin via Web Worker
   *
   * @param {number} bet - Bet amount
   * @param {string} token - Token (for interface compatibility)
   * @returns {Promise<Object>} Normalized spin result
   */
  spin(bet, token, command, purchasedFeature, spinOptions) {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      this.worker.onmessage = (event) => {
        const data = event.data;

        if (data.error) {
          const normalizedError = this.normalizeError(data.error);
          reject(normalizedError);
          return;
        }

        // Check API-level success flag
        if (data.success === false) {
          const errorCode = data.error || "Unknown error";
          const errorMsg = data.message || "";
          reject(
            this.normalizeError({
              error: { code: errorCode, message: errorMsg },
            }),
          );
          return;
        }

        // Transform response to add buffer symbols
        const transformedData = this.transformReelsSymbols(data);
        const spinResult = transformedData.data?.spinResult;

        if (!spinResult) {
          reject(
            new Error(
              "PixUpPlayRGS: Spin - Invalid response: missing spinResult",
            ),
          );
          return;
        }

        // Adapt to state format
        const adaptedRound = this.adaptSpinRoundToState(spinResult, bet);

        if (adaptedRound) {
          StorageManager.saveBaseGameRound({
            round: adaptedRound,
            bet: bet,
            balance: spinResult.balance,
          });
        }

        // Detect free spin trigger (only when free spins feature is enabled)
        const isFreeSpinTrigger =
          FEATURES_CONFIG.FREE_SPINS.ENABLED &&
          adaptedRound &&
          adaptedRound.scatterCount >= FEATURES_CONFIG.TENSION.CUTOFF_THRESHOLD;

        resolve({
          token: this.sessionId,
          bet,
          balance: spinResult.balance,
          round: adaptedRound,
          gameMode: isFreeSpinTrigger ? "freeSpins" : "base",
          featureState: isFreeSpinTrigger
            ? {
                type: "freeSpins",
                phase: "intro",
                data: {
                  freeSpinCount: spinResult.freeSpinsAwarded || 0,
                  freeSpinIndex: spinResult.freeSpinIndex || 0,
                  freeSpinTotal: spinResult.freeSpinTotal || 0,
                },
              }
            : { type: null, phase: null, data: null },
        });
      };

      this.worker.onerror = (event) => {
        reject(
          new Error(
            `PixUpPlayRGS: Worker error: ${event.message || "Unknown worker error"}`,
          ),
        );
      };

      this.worker.postMessage({
        bet,
        baseUrl: this.baseUrl,
        gameId: this.gameId,
        sessionId: this.sessionId,
        currency: this.currency,
        funMode: this.funMode,
        purchasedFeature: purchasedFeature || null,
      });
    });
  }

  /**
   * Buy bonus feature — calls the separate /buy-feature endpoint
   * No worker needed — this is a one-shot request, not a rapid spin.
   *
   * @param {number} bet - Base bet amount (backend multiplies by buy multiplier)
   * @returns {Promise<Object>} { roundId, balance, numberOfFreeSpins }
   */
  async buyFeature(bet) {
    const apiUrl = `${this.baseUrl}/game-api/${this.gameId}/api/session/${this.sessionId}/game/buy-feature`;

    try {
      const requestBody = {
        sessionId: this.sessionId,
        betAmount: bet,
        currency: this.currency,
        funMode: this.funMode,
      };
      const response = await axios.post(apiUrl, requestBody);

      if (response.data?.success === false) {
        const errorMsg = response.data.error || "Buy feature failed";
        throw new Error(`PixUpPlayRGS: Buy Feature - ${errorMsg}`);
      }

      const data = response.data?.data;
      if (!data) {
        throw new Error(
          "PixUpPlayRGS: Buy Feature - Invalid response: missing data",
        );
      }

      return {
        roundId: data.roundId,
        balance: data.playerBalance,
        numberOfFreeSpins: data.numberOfFreeSpins,
      };
    } catch (error) {
      if (error.message?.startsWith("PixUpPlayRGS:")) {
        throw error;
      }
      throw new Error(`PixUpPlayRGS: Buy Feature failed: ${error.message}`);
    }
  }

  /**
   * Get response handler configuration
   */
  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: () => false,
            extractFreeSpins: () => [],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.success === true &&
            response.data?.spinResult !== undefined,
          extractRound: (spinResult) => {
            if (!spinResult) return {};
            const transformed = { ...spinResult };
            if (
              spinResult.visibleGrid &&
              Array.isArray(spinResult.visibleGrid)
            ) {
              transformed.screen = this.transformVisibleGrid(
                spinResult.visibleGrid,
              );
            }
            if (spinResult.wins && Array.isArray(spinResult.wins)) {
              transformed.wins = this.transformWins(spinResult.wins);
            } else {
              transformed.wins = [];
            }
            return transformed;
          },
        },
      },
      propertyMap: {
        balance: "data.spinResult.balance",
        bet: undefined,
        win: "data.spinResult.totalWin",
        token: "data.roundId",
        round: "data.spinResult",
        reels: "data.spinResult.visibleGrid",
        wins: "data.spinResult.wins",
      },
    };
  }

  /**
   * Get balance management strategy
   *
   * PixUpPlay balance model:
   * - Server returns updated balance after spin
   * - Client deducts bet immediately, then syncs to server balance
   */
  getBalanceStrategy() {
    return {
      serverIncludesWin: true,
      clientDeductsBet: true,
      clientAddsWin: false,
    };
  }

  /**
   * Validate session - clear stale localStorage if sessionId changed
   * Compares current sessionId with stored session token.
   * On mismatch: clears all game data for fresh start.
   */
  validateSession() {
    const currentSession = this.sessionId;
    const storedSession = StorageManager.getStoredSessionToken();

    if (!currentSession) {
      return false;
    }

    if (currentSession !== storedSession) {
      StorageManager.clearSessionData();
      StorageManager.saveSessionToken(currentSession);
      return false;
    }

    return true;
  }

  /**
   * Get current session token
   */
  getCurrentToken() {
    return this.sessionId || null;
  }

  /**
   * Fetch current balance from the server
   * GET /api/session/:sessionId/game/balance
   *
   * @returns {Promise<{ balance: number, currency: string }>}
   */
  async getBalance() {
    const apiUrl = `${this.baseUrl}/game-api/${this.gameId}/api/session/${this.sessionId}/game/balance`;

    try {
      const response = await axios.get(apiUrl);

      if (response.data?.success === false) {
        throw new Error(response.data.error || "Balance fetch failed");
      }

      const data = response.data?.data;
      if (!data || data.balance === undefined) {
        throw new Error("PixUpPlayRGS: Balance - Invalid response");
      }

      return { balance: data.balance, currency: data.currency };
    } catch (error) {
      if (error.message?.startsWith("PixUpPlayRGS:")) throw error;
      throw new Error(`PixUpPlayRGS: Balance fetch failed: ${error.message}`);
    }
  }

  /**
   * Whether to sync balance on win close
   */
  shouldSyncBalanceOnWinClose() {
    return false;
  }

  /**
   * Normalize error response to universal format
   *
   * @param {Object|string} error - Provider-specific error
   * @returns {Object} Normalized error { errors: [{ code, desc }] }
   */
  normalizeError(error) {
    // Already in universal format
    if (error?.errors && Array.isArray(error.errors)) {
      return error;
    }

    // PixUpPlay error format: { error: "CODE", message: "..." }
    // or nested: { error: { code: "CODE", message: "..." } }
    if (error?.error) {
      const providerCode =
        typeof error.error === "string"
          ? error.error
          : error.error?.code || "UNKNOWN";
      const universalCode = PROVIDER_CONFIG.ERROR_CODES[providerCode] || 999;
      const desc =
        typeof error.error === "string"
          ? error.message || providerCode.toLowerCase()
          : error.error?.message || providerCode.toLowerCase();

      return {
        errors: [{ code: universalCode, desc }],
      };
    }

    // String errors
    if (typeof error === "string") {
      return {
        errors: [{ code: 999, desc: error }],
      };
    }

    return {
      errors: [{ code: 999, desc: "Unknown error" }],
    };
  }
}

export default PixUpPlayRGS;
